import { useEffect, useRef } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useProgress } from '../context/ProgressContext'
import { useConfig } from '../context/ConfigContext'
import { useAuth } from '../context/AuthContext'
import { modules as builtInModules, SEED_COURSES, getModule } from '../data/modules'
import { mergeCatalogue, isCourseVisible, publishedInCourse, moduleNumberIn } from '../data/catalogue'
import { groupIntoChapters, moduleHref } from '../data/courses'
import { CheckCircle2, ChevronLeft, ChevronRight, AlertTriangle, Code2, ExternalLink, Sparkles, RefreshCw } from 'lucide-react'

const PHASE_BADGE = {
  amber: 'bg-amber-100 text-amber-700',
  blue: 'bg-blue-100 text-blue-700',
  green: 'bg-green-100 text-green-700',
  purple: 'bg-purple-100 text-purple-700',
  red: 'bg-red-100 text-red-700',
  cyan: 'bg-cyan-100 text-cyan-700',
  indigo: 'bg-indigo-100 text-indigo-700',
  teal: 'bg-teal-100 text-teal-700',
  rose: 'bg-rose-100 text-rose-700',
}

export default function CoursePage() {
  const { user } = useAuth()
  const { isModuleCompleted, getModuleProgress, getReissuedModules, getStaleModules, progressForCourse } = useProgress()
  const { customModules, allCourses, loading: configLoading } = useConfig()
  const catalogue = mergeCatalogue(builtInModules, customModules || [])
  const reissuedModuleIds = getReissuedModules()
  const staleModuleIds = getStaleModules()
  const focusRef = useRef(null)
  const { courseId } = useParams()

  // Courses a trainee may see: not hidden by an admin, and with something
  // published in them. One predicate, shared with Home and the degree maths.
  const visibleCourses = (allCourses || SEED_COURSES).filter((t) => isCourseVisible(t, customModules))

  // This page is ONE course, named by the path. It used to be `/modules` with
  // an optional `?track=`, where the absence of the param meant "every module
  // in every course on one page" — which is what made a course and a module
  // impossible to tell apart. The course index is `/` now, and an id that
  // matches no course goes there rather than silently listing everything.
  const activeCourse = visibleCourses.find((t) => t.id === courseId) || null
  const shownCourses = activeCourse ? [activeCourse] : []

  // Find the module to focus: first not-completed, or the last completed one.
  // completed. Scoped to the track being shown — otherwise opening the
  // Finance course scrolls you into the middle of the Sheets one.
  // ONE pass over the course's own roster. This used to walk the built-in
  // modules and then the custom ones in two separate loops, which meant the
  // focus could land on a custom module while an earlier built-in one was
  // still unstarted. The roster is already in course order, so first-unstarted
  // now means first in the course.
  const focusModuleId = (() => {
    let lastCompleted = null
    for (const mod of publishedInCourse(activeCourse, catalogue)) {
      if (isModuleCompleted(mod.id)) {
        lastCompleted = mod.id
      } else {
        return mod.id // first unstarted module
      }
    }
    return lastCompleted
  })()

  useEffect(() => {
    if (focusRef.current) {
      // Small delay to let animations start
      const timer = setTimeout(() => {
        focusRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 300)
      return () => clearTimeout(timer)
    }
  }, [focusModuleId])

  // Hold while the courses and custom modules are still arriving. Without this
  // the page rendered a decision made from an EMPTY custom-module list: a
  // course that exists only because a custom module names it was not in
  // `visibleCourses` yet, so `activeCourse` was null and the old code fell back
  // to listing every course — Sheets Reporting first — before flipping to the
  // right one a moment later. You clicked GitHub and landed on Sheets.
  //
  // Safe to gate on, unlike the progress load: ConfigContext clears `loading`
  // in a `finally`, and on the signed-out path too, so there is no branch that
  // leaves this spinning. See docs/HANDOVER-completion-truth.md §5 for the
  // version of this mistake that was NOT bounded.
  if (configLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-blue-500" />
      </div>
    )
  }

  // Settled, and no such course. The index, rather than an empty page.
  if (!activeCourse) return <Navigate to="/" replace />

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
          >
            <ChevronLeft size={16} />
            Home
          </Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-10">
        <h1 className="text-3xl font-bold text-slate-800 mb-2">{activeCourse.name}</h1>
        <p className="text-slate-500 mb-4">{activeCourse.description}</p>
        <Link to="/" className="inline-block text-sm text-blue-600 hover:underline mb-10">
          View all courses
        </Link>

        {reissuedModuleIds.length > 0 && (
          <div className="mb-8 rounded-xl border-2 border-amber-300 bg-amber-50 p-5">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-amber-800">Retest required</h3>
                <p className="text-sm text-amber-700 mt-1">
                  Your admin has requested you retake the following module{reissuedModuleIds.length !== 1 ? 's' : ''}:
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {reissuedModuleIds.map((id) => (
                    <Link
                      key={id}
                      to={moduleHref(activeCourse?.id, getModule(id, customModules) || id)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 transition-colors"
                    >
                      {getModule(id)?.title || `Module ${id}`}
                      <ChevronRight className="w-3.5 h-3.5" />
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {staleModuleIds.length > 0 && (
          <div className="mb-8 rounded-xl border-2 border-sky-300 bg-sky-50 p-5">
            <div className="flex items-start gap-3">
              <RefreshCw className="w-5 h-5 text-sky-600 shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-sky-800">Updated since you finished</h3>
                <p className="text-sm text-sky-700 mt-1">
                  {staleModuleIds.length === 1 ? 'This module has' : 'These modules have'} changed since you
                  completed {staleModuleIds.length === 1 ? 'it' : 'them'}. Take{' '}
                  {staleModuleIds.length === 1 ? 'it' : 'them'} again to reclaim your score.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {staleModuleIds.map((id) => (
                    <Link
                      key={id}
                      to={moduleHref(activeCourse?.id, getModule(id, customModules) || id)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sky-600 text-white text-sm font-medium hover:bg-sky-700 transition-colors"
                    >
                      {getModule(id)?.title || `Module ${id}`}
                      <ChevronRight className="w-3.5 h-3.5" />
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-14">
          {shownCourses.map((track) => {
            const tp = progressForCourse(track.id)

            // Chapters belong to the course and modules name theirs, rather
            // than a `phases` array holding a second copy of the same fact.
            // courses.test.js proves this reproduces that array exactly for
            // every course in the catalogue — same chapters, same order, same
            // modules — which is what made it safe to change what trainees see.
            // The course's OWN ordered list — a module in two courses appears
            // in both, which filtering on `m.track === track.id` could not do.
            const roster = publishedInCourse(track, catalogue)
            const coursePhases = groupIntoChapters(track, roster)

            // "Module 3" is a POSITION IN THIS COURSE, not a field on the
            // module: the same module in two courses is numbered correctly in
            // each. Taken from the course roster rather than the chapter, so
            // the numbers run 1..n across the course and do not restart at
            // every heading. `startsAt` keeps Sheets opening at Module 0.
            const numberOf = new Map(
              roster.map((m, i) => [String(m.id), moduleNumberIn(track, i)]),
            )

            return (
              <section key={track.id}>
                {/* Course header with its own progress — the denominator is
                    this course only, so finishing Finance does not read as
                    35%. */}
                <div className="mb-6 pb-4 border-b border-slate-200">
                  <div className="flex items-end justify-between gap-4 flex-wrap">
                    {/* The course name and description are the h1 above, so
                        only the progress bar belongs here. */}
                    <div />
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-sm text-slate-500 tabular-nums">
                        {tp.completed}/{tp.total}
                        {tp.optionalTotal > 0 && tp.optionalCompleted > 0 && (
                          <span className="text-slate-400"> +{tp.optionalCompleted}</span>
                        )}
                      </span>
                      <div className="w-32 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-blue-500 rounded-full transition-all duration-500"
                          style={{ width: `${tp.percent}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-10">
                  {coursePhases.map((phase) => (
                    <div key={`${track.id}-${phase.name}`}>
                      <div className="flex items-center gap-3 mb-4">
                        <span
                          className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                            PHASE_BADGE[phase.color] || PHASE_BADGE.blue
                          }`}
                        >
                          {phase.name}
                        </span>
                      </div>

                      <div className="space-y-3">
                        {phase.modules
                          .map((mod, idx) => {
                            const completed = isModuleCompleted(mod.id)
                            const progress = getModuleProgress(mod.id)
                            const quizState = progress?.quizState
                            const answeredCount = quizState?.questionResults?.filter(
                              (r) => r && (r.correct || r.attempts >= 3)
                            )?.length || 0
                            const totalQuestions = quizState?.questionResults?.length || 0
                            const isReissued = reissuedModuleIds.includes(mod.id)
                            const isStale = staleModuleIds.includes(mod.id)

                            return (
                              <motion.div
                                key={mod.id}
                                ref={mod.id === focusModuleId ? focusRef : null}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: idx * 0.05 }}
                              >
                                <Link
                                  to={moduleHref(track.id, mod)}
                                  className={`block bg-white rounded-xl border p-5 transition-all hover:shadow-md ${
                                    isReissued
                                      ? 'border-amber-300 bg-amber-50/30 ring-1 ring-amber-200'
                                      : completed
                                        ? 'border-green-200 bg-green-50/30'
                                        : 'border-slate-200 hover:border-blue-300'
                                  }`}
                                >
                                  <div className="flex items-start gap-4">
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        {numberOf.get(String(mod.id)) != null && (
                                          <span className="text-xs font-medium text-slate-400">
                                            Module {numberOf.get(String(mod.id))}
                                          </span>
                                        )}
                                        {mod.optional && (
                                          <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-500">
                                            Optional
                                          </span>
                                        )}
                                        {isReissued && (
                                          <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700">
                                            Retest
                                          </span>
                                        )}
                                        {isStale && !isReissued && (
                                          <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-sky-100 text-sky-700">
                                            Updated
                                          </span>
                                        )}
                                        {completed && !isReissued && (
                                          <CheckCircle2 size={16} className="text-green-500" />
                                        )}
                                      </div>
                                      <h3 className="font-semibold text-slate-800 mt-0.5">
                                        {mod.title}
                                      </h3>
                                      <p className="text-sm text-slate-500 mt-1">
                                        {mod.description}
                                      </p>
                                      {completed ? (
                                        <div className="flex items-center gap-2 mt-2">
                                          <div className="h-1.5 flex-1 max-w-[120px] bg-slate-100 rounded-full overflow-hidden">
                                            <div className="h-full bg-green-500 rounded-full" style={{ width: '100%' }} />
                                          </div>
                                          <span className="text-xs text-green-600 font-medium">
                                            Complete
                                          </span>
                                        </div>
                                      ) : answeredCount > 0 && totalQuestions > 0 ? (
                                        <div className="flex items-center gap-2 mt-2">
                                          <div className="h-1.5 flex-1 max-w-[120px] bg-slate-100 rounded-full overflow-hidden">
                                            <div
                                              className="h-full bg-blue-500 rounded-full transition-all duration-500"
                                              style={{ width: `${(answeredCount / totalQuestions) * 100}%` }}
                                            />
                                          </div>
                                          <span className="text-xs text-slate-400">
                                            {answeredCount}/{totalQuestions} questions
                                          </span>
                                        </div>
                                      ) : null}
                                    </div>
                                    <ChevronRight size={20} className="text-slate-300 mt-2 shrink-0" />
                                  </div>
                                </Link>
                              </motion.div>
                            )
                          })}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )
          })}
        </div>

        {/* Admin Link to Build / Manage */}
        {(user?.isAdmin || user?.isManager) && (
          <div className="mt-14 rounded-2xl border border-dashed border-indigo-200 bg-indigo-50/50 p-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-xl bg-indigo-100 text-indigo-700 shrink-0 mt-0.5">
                  <Sparkles size={20} />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-slate-900">Build New Modules or Courses</h3>
                  <p className="text-sm text-slate-600 mt-0.5">
                    Create lessons using AI Studio or view documentation for custom curriculum development.
                  </p>
                </div>
              </div>
              <Link
                to="/admin/docs"
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-semibold transition-colors shadow-sm shrink-0 self-start sm:self-auto"
              >
                <Sparkles size={16} />
                Admin Guides & Studio
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
